import { ref, computed } from "vue";
import axios from "axios";
import { isLoggedIn } from "./auth";

// Global Set av favorit-event-IDs - delas mellan alla event-cards så
// hjärtan uppdateras synkront över hela sidan. Innehåller både Concert-
// och ExternalEvent-IDs (ObjectId:s är unika över collections i praktiken).
const favoriteIds = ref(new Set());
const loaded = ref(false);

function authConfig() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Avgör eventType från ett event-objekt. Vi använder _isExternal-flaggan
 * som sätts av backend i /api/events/gbg- och /favorites-svaren.
 */
function eventTypeFor(event) {
  return event && event._isExternal ? "ExternalEvent" : "Concert";
}

async function loadFavoriteIds() {
  if (!isLoggedIn.value) {
    favoriteIds.value = new Set();
    loaded.value = true;
    return;
  }
  try {
    const { data } = await axios.get(
      process.env.VUE_APP_API_URL + "favorites/ids",
      authConfig()
    );
    favoriteIds.value = new Set(data);
    loaded.value = true;
  } catch (err) {
    console.error("Kunde inte ladda favoriter:", err.message || err);
    favoriteIds.value = new Set();
    loaded.value = true;
  }
}

/**
 * Toggla favorit-status. Tar emot hela event-objektet (inte bara ID:t)
 * så vi kan avgöra eventType från _isExternal-flaggan.
 */
async function toggleFavorite(event) {
  if (!isLoggedIn.value) {
    throw new Error("not-logged-in");
  }
  const eventId = event && event._id ? String(event._id) : null;
  if (!eventId) return;
  const eventType = eventTypeFor(event);

  const set = favoriteIds.value;
  if (set.has(eventId)) {
    // Optimistisk: ta bort lokalt direkt
    set.delete(eventId);
    favoriteIds.value = new Set(set);
    try {
      await axios.delete(
        process.env.VUE_APP_API_URL + "favorites/" + eventId,
        { ...authConfig(), data: { eventType } }
      );
    } catch (err) {
      // Återställ vid fel
      set.add(eventId);
      favoriteIds.value = new Set(set);
      throw err;
    }
  } else {
    set.add(eventId);
    favoriteIds.value = new Set(set);
    try {
      await axios.post(
        process.env.VUE_APP_API_URL + "favorites/" + eventId,
        { eventType },
        authConfig()
      );
    } catch (err) {
      set.delete(eventId);
      favoriteIds.value = new Set(set);
      throw err;
    }
  }
}

function isFavorite(eventId) {
  return computed(() => favoriteIds.value.has(String(eventId)));
}

function clearFavorites() {
  favoriteIds.value = new Set();
  loaded.value = false;
}

export {
  favoriteIds,
  loaded,
  loadFavoriteIds,
  toggleFavorite,
  isFavorite,
  clearFavorites,
};
